import React, { useEffect, useRef, useState } from "react";
import { Box, Modal } from "@mui/material";
import NestedMenu from "./NestedMenu";

const ContextMenuWrapper = ({ anchorPosition, actions, onClose }) => {
  const menuRef = useRef(null);
  const [menuSize, setMenuSize] = useState({ width: 0, height: 0 });
  const [direction, setDirection] = useState({
    horizontal: "right",
    vertical: "down",
  });

  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  const isLeftSide = anchorPosition?.mouseX < windowWidth / 2;
  const isTopSide = anchorPosition?.mouseY < windowHeight / 2;

  const isMobile = window.innerWidth < 960;

  const normalizeShortcut = (s) => {
    if (!s) return null;
    if (typeof s === "string") return { key: s.toLowerCase() };
    return { ...s, key: (s.key || "").toLowerCase() };
  };

  const matchesShortcut = (e, shortcut) => {
    // Ignore if typing in inputs or contentEditable
    const target = e.target;
    const isTyping =
      target &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable);
    if (isTyping) return false;

    const key = (e.key || "").toLowerCase();
    const wantCtrl = !!shortcut.ctrl;
    const wantAlt = !!shortcut.alt;
    const wantShift = !!shortcut.shift;
    const wantMeta = !!shortcut.meta;

    return (
      key === shortcut.key &&
      !!e.ctrlKey === wantCtrl &&
      !!e.altKey === wantAlt &&
      !!e.shiftKey === wantShift &&
      !!e.metaKey === wantMeta
    );
  };

  // Flatten actions tree into a list of executable shortcuts
  const flattenShortcuts = (list, acc) => {
    if (!Array.isArray(list)) return acc;
    for (const item of list) {
      const norm = normalizeShortcut(item?.shortcut);
      if (norm && typeof item?.action === "function") {
        acc.push({ shortcut: norm, run: item.action });
      }
      if (Array.isArray(item?.items) && item.items.length) {
        flattenShortcuts(item.items, acc);
      }
    }
    return acc;
  };

  // Keep a snapshot per open to avoid re-walking on every keydown
  const [shortcutTable, setShortcutTable] = useState([]);

  useEffect(() => {
    if (!anchorPosition) {
      setShortcutTable([]);
      return;
    }
    setShortcutTable(flattenShortcuts(actions, []));
  }, [anchorPosition, actions]);

  // Listen to keydown only while the menu is open
  useEffect(() => {
    if (!anchorPosition) return;

    const onKeyDown = (e) => {
      const match = shortcutTable.find((entry) =>
        matchesShortcut(e, entry.shortcut)
      );
      if (match) {
        e.preventDefault();
        try {
          match.run();
        } finally {
          onClose && onClose();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [anchorPosition, shortcutTable, onClose]);

  useEffect(() => {
    if (menuRef.current) {
      setMenuSize({
        width: menuRef.current.offsetWidth,
        height: menuRef.current.offsetHeight,
      });
    }

    setDirection({
      horizontal: isLeftSide ? "right" : "left",
      vertical: isTopSide ? "down" : "up",
    });
  }, []);

  useEffect(() => {}, []);

  if (!anchorPosition) return null;

  let adjustedX = anchorPosition?.mouseX;
  let adjustedY = anchorPosition?.mouseY;

  if (direction.horizontal === "left") {
    adjustedX -= menuSize.width;
  }

  if (direction.vertical === "up") {
    adjustedY -= menuSize.height;
  }

  if (isMobile) return <NestedMenu actions={actions} onClose={onClose} />;
  else
    return (
      <Box
        ref={menuRef}
        sx={{
          position: "absolute",
          top: `${adjustedY}px`,
          left: `${adjustedX}px`,
          zIndex: 1201,
          visibility: menuSize.width > 0 ? "visible" : "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <NestedMenu
          actions={actions}
          onClose={onClose}
          isLeftSide={isLeftSide}
          isTopSide={isTopSide}
        />
      </Box>
    );
};

export default ContextMenuWrapper;
